import { FunctionComponent, ReactNode } from "preact/compat";
import { WorkflowManager } from "./WorkflowManager";
import type { RenderableContextService } from "@spiffcommerce/preview";
import type { ConversionConfiguration, ThemeInstallConfigurationGraphQl } from "@spiffcommerce/theme-bridge";
import { CanvasCommand } from "./command";

export enum AssetType {
    Data = "Data",
    Font = "Font",
    Frame = "Frame",
    Illustration = "Illustration",
    Image = "Image",
    Model = "Model",
    Material = "Material",
    Color = "Color",
    QuestionnaireCollateral = "QuestionnaireCollateral",
    RequestCollateral = "RequestCollateral",
    SignupCollateral = "SignupCollateral",
    Video = "Video",
    ColorProfile = "ColorProfile",
    Environment = "Environment",
}

export interface Asset {
    /**
     * The path of the asset within the bucket.
     */
    key?: string;

    /**
     * The name of this asset, the file name by default, otherwise a name set by the user.
     */
    name?: string;

    /**
     * The time that this asset was uploaded.
     */
    createdAt?: string;

    /**
     * The ID of this asset's license.
     */
    licenseId?: string;

    /**
     * The content type registered with the bucket.
     */
    mimeType?: string;

    /**
     * The ID of the person that uploaded this asset.
     */
    ownerId?: string;

    /**
     * The price to use this asset.
     */
    price?: number;

    /**
     * The asset type being dealt with.
     */
    type?: AssetType;

    /**
     * True when this asset is publicly available to anyone.
     */
    public?: boolean;

    /**
     * All versions for this asset, including the "latest" one which is the default and represented
     * by the key of the asset itself.
     */
    objectVersions?: AssetObjectVersion[];

    /**
     * A list of tags for organisational purposes.
     */
    tags?: AssetTag[];

    hubLink?: string;

    fileLink?: string;

    newVersionLink?: string;

    invalidateLink?: string;

    versions?: AssetVersion[];

    metadata?: AssetMetadata[];

    assetConfiguration?: AssetConfiguration;
}

export interface AssetConfiguration {
    colorOption?: OptionResource;
    channelNumbers: {
        id: string;
        number: number;
    }[];
    defaultColorVariants: {
        channelNumber: number;
        variant: VariantResource;
    }[];
}

/**
 * A tag for organisation of assets
 */
export interface AssetTag {
    /**
     * A unique ID.
     */
    id: string;
    /**
     * The human friendly display name of the tag.
     */
    name: string;
    /**
     * The key of the asset the tag is associated to.
     */
    assetKey: string;
    /**
     * The partner that owns that asset & tag.
     */
    partnerId: string;
}

interface AssetMetadata {
    key: string;
    value: string;
}

interface AssetVersion {
    name: string;

    link: string;
}

/**
 * An object version is a snapshow of an asset at a given point in time. Each time
 * the user updates the file associated to an asset a new object version is created.
 * Users can then see a timeline of all object versions and switch between them.
 */
export interface AssetObjectVersion {
    /**
     * The size of the file in bytes.
     */
    size: number;
    /**
     * A timestamp for when this version was created.
     */
    timestamp: string;
    /**
     * The id of the version, this is required in cases where we want to perform
     * specific operations on a version such as making it the latest one or deleting it.
     */
    versionId: string;
    /**
     * The url that this asset should be called on when we want to get it,
     * instead of the base key. This key is
     */
    versionedKey: string;
}

export interface Step<T extends AnyStepData = AnyStepData> {
    conditions?: Condition[];
    data: T;
    globalPropertyAspectConfigurations?: GlobalPropertyAspectConfiguration[];
    helpText?: string;
    mandatory?: boolean;
    relevantPanelName?: string;
    silent?: boolean;
    option?: OptionResource;
    optionId?: string;
    stepName: string;
    stepTitle: string;
    tags?: string[];
    type: StepType;
}

export interface GlobalPropertyAspectConfiguration {
    aspectName?: string;
    globalPropertyConfigurationId?: string;
}

export interface GlobalPropertyConfiguration {
    id: string;
    aspects: GlobalPropertyConfigurationAspect[];
}

export interface GlobalPropertyConfigurationAspect {
    name: string;
    type: AspectType;
    title: string;
    description: string;
    entityId?: string;
    conditions?: GlobalPropertyConfigurationAspectCondition[];
    data?: GlobalPropertyConfigurationAspectData;
}

export interface GlobalPropertyConfigurationAspectData {
    fileUpload: GlobalPropertyConfigurationAspectFileUploadData;
}

export interface GlobalPropertyConfigurationAspectFileUploadData {
    colorPickerEnabled?: boolean;
    pmsPickerEnabled?: boolean;
    colorOptionId?: string;
}

export interface GlobalPropertyConfigurationAspectCondition {
    targetAspectName?: string;
    action: AspectConditionAction;
    requiredVariantSelections: string[];
}

export enum AspectConditionAction {
    Show = "Show",
}

export enum AspectType {
    FileUpload = "FileUpload",
    Option = "Option",
    ColorOption = "ColorOption",
    Text = "Text",
}

export enum StepType {
    Information = "Information",
    Bulk = "Bulk",
    DigitalContent = "DigitalContent",
    Finish = "Finish",
    Frame = "Frame",
    Illustration = "Illustration",
    Introduction = "Introduction",
    Material = "Material",
    Model = "Model",
    Module = "Module",
    Picture = "Picture",
    /**
     * @deprecated Use Frame instead.
     */
    Photo = "Photo",
    ProductOverlay = "ProductOverlay",
    Question = "Question",
    Shape = "Shape",
    SilentIllustration = "SilentIllustration",
    Text = "Text",
}

export interface StepAspect {
    stepName: string;
    stepType: StepType;
    aspectType: StepAspectType;
}

export enum StepAspectType {
    Color = "Color",
    Colors = "Colors",
    Selection = "Selection",
    Selections = "Selections",
    Text = "Text",
    Upload = "Upload",
}

export interface Region {
    top: number;
    left: number;
    width: number;
    height: number;
    layer?: number;
    layerIndex?: number;
    rotation: number;
    panelId: string;
    immutable?: boolean;
    adjustmentBoundary?: { x: number; y: number; height: number; width: number };
}

export interface Condition {
    requiredVariantSelections: string[];
    action: string;
    targetStepName: string;
}

interface StepAsset {
    key: string;
    previewKey?: string;
}

export interface OptionResource {
    id?: string;
    localId?: string;
    name: string;
    type?: string;
    variants?: VariantResource[];
    defaultVariant?: VariantResource;
    workflowId?: string;
    public?: boolean;
    displayType?: string;
    colorProfile?: Asset;
    integrationOptions?: IntegrationOptionResource[];
}

export interface VariantResource {
    id?: string;
    name: string;
    enabled: boolean;
    priceModifier: number;

    asset?: Asset;
    material?: MaterialResource;
    thumbnail?: Asset;

    color?: string;
    localId?: string;
    default?: boolean;
    namedColor?: string;

    option?: OptionResource;
}

export interface IntegrationOptionResource {
    id?: string;
    localId?: string;
    integrationId: string;
    externalProductId?: string;
}

/**
 * Represents a material resource that can be additively applied to
 * a material targeted in the scene.
 */
export type MaterialResource = {
    /**
     * A unique identified for this material.
     */
    id: string;
    /**
     * The name of this material
     */
    name: string;
    /**
     * Defines the base color of a surface before any other calculations are made.
     */
    albedoMapKey?: string;
    /**
     * Defines the transparency of a surface.
     */
    alphaMapKey?: string;
    /**
     * Defines shadowing on a surface.
     */
    ambientMapKey?: string;
    /**
     * Defines the amount of light being emitted from a surface.
     */
    emissionMapKey?: string;
    /**
     * Identical to roughness.
     */
    metallicMapKey?: string;
    /**
     * Defines the direction light will bounce in when it hits a point on a surface.
     */
    normalMapKey?: string;
    /**
     * Used to define how smooth a surface is.
     */
    roughnessMapKey?: string;
    /**
     * Used to define refraction of light on a surface.
     */
    refractionMapKey?: string;
    /**
     * The intensity of refraction, when refraction is enabled via a texture.
     */
    refractionIntensity?: number;
    /**
     * Use to define reflection of light on a surface.
     */
    reflectionMapKey?: string;
    /**
     * The intensity of reflection, when reflection is enabled via a texture.
     */
    reflectionIntensity?: number;
    /**
     * The rotation of the reflection map (around the up axis, in degrees), when reflection is enabled via a texture.
     */
    reflectionRotation?: number;
    /**
     * When enabled the material will be displayed with a clearcoat affect for simulating coated plastic surfaces.
     */
    clearCoat: MaterialEffectMode;
    /**
     * Index of refraction when clear coat is enabled.
     */
    clearCoatIOR?: number;
    /**
     * The date that this material resource was created
     */
    createdAt: string;
    /**
     * The date that this material resource was last updated.
     */
    updatedAt: string;
};

/**
 * Used to specify the behavior of a material effect such as clearcoat, sheen and translucency.
 */
export enum MaterialEffectMode {
    /**
     * When a material variant effect specifies 'None' the effect doesn't change in any way. This is the default behavior.
     */
    None = "None",
    /**
     * When a material variant effect specifies 'RemoveWhenSelected' the effect is removed.
     */
    RemoveWhenSelected = "RemoveWhenSelected",
    /**
     * When a material variant effect specifies 'ApplyWhenSelected' the effect is enabled.
     */
    ApplyWhenSelected = "ApplyWhenSelected",
}

export type AnyStepData =
    | InformationStepData
    | BulkStepData
    | DigitalContentStepData
    | FinalizeStepData
    | FrameStepData
    | IllustrationStepData
    | MaterialStepData
    | ModelStepData
    | ModuleStepData
    | PictureStepData
    | QuestionStepData
    | ShapeStepData
    | SilentStepData
    | TextStepData;

export interface Animatable {
    lookAtAnimation?: CameraAnimation;
    modelAnimation?: ModelAnimation;
}

export interface BulkStepData extends Animatable {
    aspects: StepAspect[];
}

export interface InformationStepData extends Animatable, Placeable {
    content: string;
}

export interface DigitalContentStepData extends Animatable, Placeable {
    baseUrl: string;
    varyUpload?: boolean;
}

export interface FrameStepData extends Animatable, Placeable, Colorable {
    hideSelectionInCart?: boolean;
    hideImageInCart?: boolean;
    colorPickerEnabled?: boolean;
    displaySelectionOnFinishStep?: boolean;
    displayImageOnFinishStep?: boolean;
    disablePlaceholder?: boolean;
    initialZoomLevel?: number;
    markSelectionAsCustomField?: boolean;
    markImageAsCustomField?: boolean;
    maxColors?: number;
    focalBlur?: boolean;
    focalBlurRadius?: number;
    focalBlurStrength?: number;
    forceImageCover?: boolean;
    overlayImageKey?: string;
    placeholderImageKey?: string;
    placeholderImageUrl?: string;
    varySelection?: boolean;
    varyUpload?: boolean;
    whitelistedExtensions: string[];
}

export interface IllustrationStepData extends Animatable, Placeable, Colorable {
    hideSelectionInCart?: boolean;
    hideColorsInCart?: boolean;
    displaySelectionOnFinishStep?: boolean;
    displayColorsOnFinishStep?: boolean;
    markSelectionAsCustomField?: boolean;
    markColorsAsCustomField?: boolean;
    assetKeys?: string[];
    assets?: StepAsset[];
    defaultAssetKey?: string;
    colorPickerEnabled?: boolean;
    pmsPickerEnabled?: boolean;
    varySelection?: boolean;
    varyColors?: boolean;
}

export interface MaterialStepData extends Animatable {
    hideSelectionInCart?: boolean;
    displaySelectionOnFinishStep?: boolean;
    markSelectionAsCustomField?: boolean;
    targetMaterials: string[];
    varySelection?: boolean;
}

export interface ModelStepData extends Animatable {
    replaceProductModel?: boolean;
    hideSelectionInCart?: boolean;
    displaySelectionOnFinishStep?: boolean;
    markSelectionAsCustomField?: boolean;
    varySelection?: boolean;
}

export interface ModuleStepData extends Animatable, Placeable {
    hideTextInCart?: boolean;
    displayTextOnFinishStep?: boolean;
    markTextAsCustomField?: boolean;
    module: string;
    maxLength: number;
    varyText?: string;
}

export interface PictureStepData extends Animatable, Placeable {
    hideSelectionInCart?: boolean;
    displaySelectionOnFinishStep?: boolean;
    markSelectionAsCustomField?: boolean;
    varySelection?: boolean;
}

export interface QuestionStepData extends Animatable {
    hideSelectionsInCart?: boolean;
    displaySelectionsOnFinishStep?: boolean;
    markSelectionsAsCustomField?: boolean;
    varySelections?: boolean;
}

export interface ShapeStepData extends Animatable, Placeable {
    hideSelectionInCart?: boolean;
    displaySelectionOnFinishStep?: boolean;
    markSelectionAsCustomField?: boolean;
    defaultColour?: string;
    excludeFromPrint?: boolean;
    varySelection?: boolean;
}

export interface TextStepData extends Animatable, Placeable, Colorable {
    hideSelectionInCart?: boolean;
    hideFillImageInCart?: boolean;
    hideColorInCart?: boolean;
    hideStrokeInCart?: boolean;
    hideTextInCart?: boolean;
    displaySelectionOnFinishStep?: boolean;
    displayImageFillOnFinishStep?: boolean;
    displayColorOnFinishStep?: boolean;
    displayTextOnFinishStep?: boolean;
    displayStrokeOnFinishStep?: boolean;
    markSelectionAsCustomField?: boolean;
    markFillImageAsCustomField?: boolean;
    markColorAsCustomField?: boolean;
    markStrokeAsCustomField?: boolean;
    markTextAsCustomField?: boolean;
    allowNewlines?: boolean;
    defaultText: string;
    deleteDefaultOnFocus?: boolean;
    replaceableText?: string;
    font: string;
    colorPickerEnabled?: boolean;
    maxLength: number;
    maxSize?: number;
    minSize?: number;
    size: number;
    textAlign: string;
    colour: string;
    uppercase?: boolean;
    vertical?: boolean;
    verticalAlign: string;
    curved?: boolean;
    paths?: string[];
    userCanReplaceText?: boolean;
    varySelection?: boolean;
    varyText?: boolean;
    varyColor?: boolean;
    imageFillOption?: OptionResource;
    imageFillScale?: number;
    strokeEnabled?: boolean;
    strokeOption?: OptionResource;
    strokeThickness?: string;
}

export interface SilentStepData extends Animatable, Placeable {
    asset?: Asset;
    excludeFromPrint?: boolean;
}

type FinalizeStepData = Animatable;

/**
 * A colorable step is a step that can have an optional color option assigned to help drive color palette choices.
 */
export interface Colorable {
    colorOption?: OptionResource;
}

/**
 * A placeable step is a step that can be placed on a canvas in a set of specific positions.
 */
export interface Placeable {
    regions: Region[];
}

export interface StepService<T extends AnyStepData> {
    /**
     * Initialize the given step, or reload from serialized data if present.
     */
    init(stepData: Step<T>, workflowManager: WorkflowManager, reducerState?: LayoutsState): Promise<any>;
}

export interface LayoutsState {
    layouts: { [id: string]: LayoutState };
    serializableWorkflow: SerializableWorkflow;
}

/**
 * A workflow step in the process of being serialized or deserialized.
 */
export interface SerializableStep {
    stepName: string;
    storage?: StepStorage;
    selectedVariants?: SerializableVariant[];
}

export interface SerializableWorkflow {
    steps: SerializableStep[];
}

export interface SerializableVariant {
    id: string;
    priceModifier: number;
}

// Non-variant information required to rehydrate a workflow step.
export interface StepStorage {
    bulkIsConnected?: boolean;
    color?: string;
    colorProfileAssetKey?: string;
    colors?: { [key: string]: IllustrationColorObject };
    colour?: string;
    currentFrameSources?: string[];
    customiseAllText?: boolean;
    defaultCleared?: boolean;
    fillImage?: TextFillImage;
    frameOffsetsList?: FrameOffsets[];
    framePatternData?: {
        originalAssetKey?: string;
        backgroundRemovedAssetKey?: string;
        useOriginalAsset?: boolean;
    };
    framePatternSrc?: string;
    inputText?: string;
    overrideGlobalConfiguration?: boolean;
    overrideGlobalConfigurations?: { [key: string]: boolean };
    strokeColor?: IllustrationColorObject;
    text?: string;
    videoShortUrl?: string;
    videoUrl?: string;
}

export interface IllustrationColorObject {
    browserValue: string;
    spotColor?: { profileName: string; namedColor: string };
    pmsValue?: string;
}

export interface FrameOffsets {
    y: number;
    x: number;
    zoom: number;
}

export interface LayoutState {
    /**
     * The elements currently on this layout.
     */
    elements: LayoutElement[];
    /**
     * A file describing the properties of this layout.
     */
    layout: ILayout;
    /**
     * A unique ID regenerated each time this layout is changed.
     */
    modificationID: string;
}

/**
 * Represents an element on the SVG canvas. This may comprise of multiple SVG elements when
 * output such as path elements, defs, etc. All concrete elements must implement this interface.
 */
export interface LayoutElement extends Layerable {
    /**
     * The unique identifier for this element.
     */
    id: string;
    /**
     * If the element was made from a workflow step, this is its step name.
     */
    stepName?: string;
    /**
     * If the element was made from a workflow step, this is its region.
     */
    stepRegion?: Region;
    /**
     * The type of this element
     */
    type: LayoutElementType;
    /**
     * Horizontal offset from top left origin point.
     */
    x: number;
    /**
     * Vertical offset from top left origin point.
     */
    y: number;
    /**
     * The height in pixels of the element.
     */
    width: number;
    /**
     * The height in pixels of the element.
     */
    height: number;
    /**
     * The rotation in degrees.
     */
    rotation: number;

    /**
     * The element is considered immutable and shouldn't be modifiable by the user.
     */
    immutable?: boolean;
    /**
     * When true this element should be rendered on top of all other elements always.
     */
    productOverlay?: boolean;
    /**
     * When true, this element will be removed from SVGString output when
     * the removeExcludedElements flag is set to true.
     */
    excludeFromExport?: boolean;
    /**
     * Value to use as the mask attribute if the element does not correspond to a step.
     */
    mask?: string;
    /**
     * Injected by the LayoutsProvider if required. Shouldn't be
     * used by the user. This is a simple way to pass global rendering configuration
     * to individual elements without having to set up a react context.
     */
    _renderingConfiguration?: RenderingConfiguration;
}

/**
 * Represents a layerable entity. Layerable entities can be sorted within our system for rendering purposes.
 */
export interface Layerable {
    /**
     * The layer that this element exists on. Elements on a higher layer can never
     * be below an element on a lower layer. 0 by default.
     */
    layer?: number;
    /**
     * The index of this element on the current layer, if no layer is present it is assumed to be 0.
     * For the index 0 is lower, > 0 is higher
     */
    layerIndex?: number;
}

export interface TextFillImage {
    src: string;
    width: number;
    height: number;
    scale: number;
}

/**
 * The reason we need to turn a layout into an SVG.
 */
export enum LayoutRenderingPurpose {
    ThreeD = "ThreeD",
    FreeDesign = "FreeDesign",
    Print = "Print",
}

/**
 * Specifies configuration for rendering a Papyrus canvas as an SVG string.
 */
export interface RenderingConfiguration {
    /**
     * Sets the viewbox to a custom value when subregions are desired.
     */
    region?: PreviewRegion;
    /**
     * Excludes marked elements from the SVG output when true.
     */
    removeExcludedElements?: boolean;

    purpose: LayoutRenderingPurpose;

    /**
     * When true any cached data will be ignored and assets will be rendered in full
     * with the properties they are provided.
     */
    omitCachedFields?: boolean;
    /**
     * When true, spot colors will be output, when provided, into any
     * fill or stroke style in an illustration.
     */
    spotColors?: boolean;

    colorProfiles?: ColorProfileProps[];

    /**
     * Allow for debug rendering on elements.
     */
    debug?: boolean;
}

export interface ColorProfileProps {
    name: string;
    key: string;
}

export interface PreviewRegion {
    top: number;
    left: number;
    width: number;
    height: number;
}

/**
 * The possible types for an element.
 */
export enum LayoutElementType {
    Frame = "frame",
    Group = "group",
    Image = "image",
    Pattern = "pattern",
    Illustration = "illustration",
    Textbox = "textbox",
}

/**
 * Represents a layout contained within a specific user created design.
 */
export interface ILayout {
    /**
     * The internal identifier for this layout.
     */
    id: string;

    /**
     * A link to the json resource describing this layout
     */
    layoutPath?: string;

    /**
     * The index this layout appears at in its design
     */
    index: number;

    /**
     * An id for the panel associated to this layout.
     */
    panelId: string;

    /**
     * The date this layout was created.
     */
    createdAt: Date;

    /**
     * The date this layout was last updated.
     */
    updatedAt: Date;

    /**
     * The height of the layout. This matches the background element on the canvas as a convenience.
     */
    height: number;

    /**
     * The width of the layout. This matches the background element on the canvas as a convenience.
     */
    width: number;
    /**
     * When true this layout should contain no background fill.
     */
    transparentBackground?: boolean;

    previewRegion?: {
        height: number;
        width: number;
        top: number;
        left: number;
    };

    useEditableArea?: boolean;
    editableArea?: {
        x: number;
        y: number;
        height: number;
        width: number;
    };

    name: string;
    title?: string;
}

export interface FileInfo {
    /**
     * The name of the file.
     */
    name: string;

    /**
     * A blob object representing the
     * data of the file.
     */
    blob: Blob;
}

/**
 * A command along with a function to run afterwards.
 */
export interface CommandWithFollowup {
    command?: CanvasCommand;
    followup?: () => Promise<void>;
}

export interface DesignResource {
    metadata?: { [key: string]: string };
    sku?: string;
}

export interface DesignDetails {
    name: string;
    layouts: { index: number; panelId: string }[];
    workflowId: string;
    transactionId: string;
    previewImage?: string;
    useThreeDimPreview: boolean;
    metadata?: { key: string; value: string }[];
    selectedVariants?: { key: string; ids: string[] }[];
}

/**
 * The variant selections of a completed step.
 */
export interface SelectedVariants {
    [stepName: string]: {
        id: string;
        name: string;
        priceModifier: number;
    }[];
}

// The metadata and variant selections of a completed workflow.
export interface ExportedData {
    [name: string]: {
        value: string;
        priceModifier: number;
    };
}

export interface DesignCreationMessage {
    additionalExternalProductId?: string;
    additionalExternalVariantId?: string;
    baseCost?: number;
    designExternalVariants?: DesignExternalVariant[];
    designProductId?: string;
    designProductVariantId?: string;
    event: string;
    exportedData: ExportedData;
    externalCartProductId?: string;
    externalCartProductVariantId?: string;
    lineItemImageUrl: string;
    metadata?: { [stepName: string]: string };
    optionsCost: number;
    processExecutionId?: string;
    quantity?: number;
    selectedVariants?: SelectedVariants;
    sku?: string;
    transactionId: string;
    transactionOwnerId?: string;
    weight?: number;
    workflowViewerLink: string;
    workflowViewerReadOnlyLink: string;
}

export enum BundleDesignCreationCartAddMode {
    /** Uses default grouping method, which consolidates products into as few line items as possible */
    Default = "Default",
    /** Only adds the first transaction to the cart. */
    FirstTransaction = "FirstTransaction",
    /** Adds each transaction in the Bundle to the cart, without consolidation. */
    OneToOne = "OneToOne",
}

export interface BundleDesignCreationMessage {
    bundleId: string;
    items: DesignCreationMessage[];
    bundleOwnerId?: string;
    cartAddMode?: BundleDesignCreationCartAddMode;
}

export interface DesignWorkflowMetadata {
    [key: string]: string;
}

/**
 * Represents a transaction.
 */
export interface Transaction {
    /**
     * A unique identifier for this transaction. Useful for reloading and
     * modifying state of the transaction.
     */
    id: string;

    /**
     * User-supplied name for the associated design.
     */
    designName?: string;

    /**
     * The workflow associated with this transaction.
     */
    workflowId?: string;

    /**
     * Temporary ID that grants write permission to the transaction.
     */
    transactionOwnerId?: string;

    /**
     * The current state of the design.
     */
    workflowState?: string;

    /**
     * URL to a partner-specific logo intended to display during the workflow experience.
     */
    customLogoLink?: string;

    /**
     * URL to a logo intended to display at the periphery of the workflow experience.
     */
    workflowFooterLogoLink?: string;

    /**
     * URL to the corresponding integration in the REST API.
     */
    restApiIntegrationLink?: string;

    /**
     * URL to redirect to when a workflow is finished.
     */
    callbackUrl?: string;

    /**
     * Product that this transaction belongs to.
     */
    product?: Product;

    /**
     * The integration product related to this lineitem.
     */
    integrationProduct?: IntegrationProductResource;

    /**
     * Whether this transaction has previously been ordered.
     */
    isOrdered?: boolean;

    /**
     * @deprecated use isOrdered to check whether transaction has been ordered instead.
     */
    lineItem?: LineItem;

    /**
     * The external product variant ID representing the design product related
     * to this transaction, null unless the createDesignProduct flag was set
     * and the design was finalized using createDesign operation.
     */
    /**@deprecated use externalCartProductVariantId or designExternalVariants[x].externalProductVariantId instead of this moving forward */
    externalDesignProductVariantId?: string;

    /**
     * The external product id representing the design product related
     * to this transaction, null unless the createDesignProduct flag was set
     * and the design was finalized using createDesign operation.
     */
    /**@deprecated use externalCartProductId or designExternalVariants[x].externalProductId instead of this moving forward. */
    externalDesignProductId?: string;

    /**
     * The external product id representing the product related to the transaction.
     * This value can hold any type of transaction such as design and standard products.
     */
    externalCartProductId?: string;

    /**
     * The external product variant id representing the product related to the transaction.
     * This value can hold any type of transaction such as design and standard products.
     */
    externalCartProductVariantId?: string;

    priceModifierTotal?: number;

    /**
     * URL to open the transaction in the workflow viewer.
     */
    workflowViewerLink?: string;

    workflowViewerReadOnlyLink?: string;

    previewImageLink?: string;

    lastSyncedAt?: string;

    /**
     * The users who have access to this transaction.
     */
    stakeholders?: Stakeholder[];

    /** The stakeholder of the currently logged in user, if applicable. */
    currentStakeholder?: Stakeholder;

    recipient?: Recipient;

    /**
     * The amount of this transaction that was, or will be, ordered
     */
    quantity?: number;

    createdAt?: string;

    printFileUrl1?: string;
    printFileUrl2?: string;
    printFileUrl3?: string;
    printFileUrl4?: string;
    printFileUrl5?: string;
    printFileName1?: string;
    printFileName2?: string;
    printFileName3?: string;
    printFileName4?: string;
    printFileName5?: string;

    /**
     * An array of objects containing information on additional products to add to the cart.
     * Typically only available when being passed to {@link DesignCreationMessage}
     */
    designExternalVariants?: DesignExternalVariant[];

    /**
     * A list of actions that have been associated to this transaction. To be used by the client to interact further post design.
     */
    transactionShareActions?: ShareAction[];

    /** Whether any share actions for this Transaction require the user to be authenticated as a stakeholder */
    hasAuthenticatedActions?: boolean;

    marketplaceThemeInstallConfiguration?: ThemeInstallConfigurationGraphQl;

    completed?: boolean;
}

export interface Recipient {
    id?: string;
    firstName?: string;
    lastName?: string;
    address?: string;
    suburb?: string;
    state?: string;
    email?: string;
    postalCode?: string;
    country?: string;
    mobile?: string;
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string;
}

export interface ShareAction {
    id: string;
    type: ShareActionType;
    title?: string;
    precedence?: number;
    stakeholderType?: string;
    url?: string;
}

export enum ShareActionType {
    Hyperlink = "Hyperlink",
    Edit = "Edit",
    Approve = "Approve",
}

/**
 * An object containing ids for an external product/variant that should be added to the cart alongside the base product/variant.
 */
export interface DesignExternalVariant {
    id?: string;
    externalProductId?: string;
    externalProductVariantId?: string;
    skuCode?: string;
}

/**
 * An integration product represents the connection of a product in SpiffCommerce with
 * a product on a third party platform.
 */
export interface IntegrationProductResource {
    /**
     * The ID of the IntegrationProduct entity. Used internally by SpiffCommerce.
     */
    id: string;
    /**
     * The ID of the product on the third party platform. For example a Shopify product ID.
     */
    externalProductId?: string;
    /**
     * The integration that this product is associated with. For example a Shopify integration.
     */
    integration?: Integration;

    /**
     * The ID of an additional product (on the third party platform) that should be added to the cart.
     */
    additionalExternalProductId?: string;

    /**
     * A supplementary ID for the additional product. Shopify uses the variant ids to add products to the cart.
     */
    additionalExternalVariantId?: string;

    /**
     * When the additionalExternalProductId is set, this is the IntegrationProduct that is associated with it.
     * If no Spiff product has been associated with the additionalExternalProductId, this will be null.
     */
    additionalIntegrationProduct?: IntegrationProductResource;

    /**
     * The product that this integration product is associated with.
     */
    product?: Product;
}

export interface Integration {
    id: string;
    enabled: boolean;
    externalIntegrationId?: string;
    type?: IntegrationType;
    isCurrent?: boolean;
    theme: Theme;
    logo: string;
    partner: Partner;
    marketplaceThemeInstallConfiguration?: ThemeInstallConfigurationGraphQl;
    name?: string;
}

export interface Theme {
    configuration?: string;
    fontAsset?: Asset;
    id?: string;
    name?: string;
    primaryColor?: string;
    secondaryColor?: string;
    textColor?: string;
    themeLayout?: {
        id?: string;
        name?: string;
    };
}

export enum IntegrationType {
    Hub = "Hub",
    Shopify = "Shopify",
}

/**
 * Fields found in a variant within a line item resource.
 */
export interface LineItemVariant {
    currencyCode: string;
    optionName: string;
    priceModifier: number;
    stepName: string;
    variantName: string;
}

export interface LineItem {
    id: string;
    transactionId?: string;
    previewImageUrl?: string;
    product: Product;
    quantity: number;
    metadata?: LineItemMetadata[];
    variants: LineItemVariant[];
}

export interface LineItemMetadata {
    stepName: string;
    metadata: string;
}

export interface ProductWorkflow {
    friendlyName: string;
    id: string;
    index?: number;
    present?: boolean;
    imageUrl: string;
    workflowName: string;
}

export interface Partner {
    id?: string;
    name?: string;
    currencyCode?: string;
    customerDetailsPromptMarkdown?: string;
    activeAddons?: AddonHandle[];
}

export enum AddonHandle {
    BackgroundRemover = "BackgroundRemover",
    ConversionAccelerator = "Conversion Accelerator",
    ProcessBuilder = "ProcessBuilder",
}

export interface Product {
    /**
     * ID of the partner owner.
     */
    partnerId?: string;
    partner?: Partner;

    /**
     * Whether a line item for this product is delivered as soon as the order is received.
     */
    autoprint: boolean;

    /**
     * Whether this product can be quickprinted.
     */
    canQuickprint?: boolean;

    /**
     * The internal identifier for this product.
     */
    id: string;

    /**
     * The human friendly name of the product.
     */
    name: string;

    /**
     * A description of the Product, written in Markdown.
     */
    description?: string;

    /**
     * A URL to the image asset associated with this product.
     */
    imageUrl?: string;

    /**
     * A resource url for a 3D model used to represent this product
     * in the 3d editor. If not available we assume the product doesn't support 3D.
     */
    modelUrl?: string;

    /**
     * A URL for the image used as an overlay on any workflows
     * that incorporate a product overlay step.
     */
    overlayImageUrl?: string;

    /**
     * Words which can't be used in a workflow for this product.
     */
    profanities?: {
        id: string;
        createdAt: string;
        userId: string;
        word: string;
    }[];

    /**
     * The max characters that can be typed for quickprint.
     */
    quickprintMaxLength?: number;

    /**
     * The name of the module to use for quickprint.
     */
    quickprintModuleName?: string;

    /**
     * If true, the product should be displayed in the client as if it is available.
     * Should be displayed as if it is unavailable otherwise.
     */
    enabled: boolean;

    /**
     * Workflows which have been assigned to this product.
     */
    workflows?: ProductWorkflow[];

    /**
     * The base price of this product in sub units. Essentially the cost of the
     * product without any customisations applied.
     */
    basePrice?: number;

    /**
     * The weight of this product.
     */
    weight?: number;

    minimumOrderQuantity?: number;

    /**
     * When specified, represents a pre-rendered image of this product to be
     * displayed to customers while the 3D preview is loading.
     */
    preloadImageUrl?: string;

    /**
     * When set the store owner has configured this product to prompt for
     * customer details. In hosted experience, we'll offer to collect this information from the customer. In
     * bespoke UIs created using the Core SDK you will need to check this flag and request the data accordingly.
     * See attachCustomerDetails on the WorkflowExperience for how to supply us with the details you collect.
     */
    promptForCustomerDetails?: boolean;

    /**
     * Configuration for conversion. Optional.
     */
    conversionConfiguration?: ConversionConfiguration;

    integrationProducts?: IntegrationProductResource[];

    productTags?: ProductTag[];

    productImages?: ProductImage[];

    /**
     * The SKU of the product in the merchant's store.
     */
    sku?: string;

    /**
     * The Spiff SKU code for the product.
     */
    skuCode?: string;

    /**
     * Price breaks for this product when dealing with quantity.
     */
    priceBreaks?: ProductPriceBreak[];
}

export interface ProductImage {
    id: string;
    precedence?: number;
    asset: Asset;
}

/**
 * A price break represents a minimum quantity of a product and the price that will be charged for that quantity.
 * This can be seen as an override for the base price of the product.
 */
export interface ProductPriceBreak {
    /**
     * The id of this price break.
     */
    id: string;
    /**
     * The minimum quantity of this price break. A positive integer.
     */
    minQty: number;
    /**
     * A percentage to apply to the overall price. A value between 0-1 where 1 retains 100% of the original price and 0 completely discounts it to free.
     */
    percentage: number;
}

export interface ProductTag {
    id: string;
    name: string;
}

export interface ColorOption {
    id?: string;
    fill?: string;
    stroke?: string;
    variant?: VariantResource;
    colorProfileAssetKey?: string;
    pmsValue?: string;
}

export interface RegionElement {
    id: string;
    region?: Region;
}

export interface FrameMetadata {
    image: string;
}

export interface IllustrationMetadata {
    colors: string[];
}

export interface ModuleMetadata {
    text: string;
}

export interface TextMetadata {
    color?: string;
    text: string;
    fillImage?: string;
    strokeColor?: string;
}

export interface SelectionStorage {
    selectedVariants?: VariantResource[];
}

export interface FrameStepStorage extends SelectionStorage {
    framePatternAsset?: Asset;
    currentFrameSources?: string[];
    framePatternData?: {
        originalAssetKey?: string;
        backgroundRemovedAssetKey?: string;
        useOriginalAsset?: boolean;
    };
}

export interface TextStepStorage extends SelectionStorage {
    color?: string;
    defaultCleared?: boolean;
    inputText?: string;
    text?: string;
    customiseAllText?: boolean;
}

export type StepSelections = {
    [key: string]: SelectionStorage;
};

export interface FrameData {
    /**
     * The path data for a frame SVG
     */
    path: string;

    /**
     *  The viewbox width
     */
    width: number;

    /**
     * The viewbox height.
     */
    height: number;
}

export interface FrameThresholdSettings {
    // Whether to enable a black and white threshold.
    useThreshold: boolean;
    // If using threshold, whether to invert black and white.
    invertThreshold: boolean;
    // An integer from 0 to 256.
    threshold: number;
    // A number from 0 to 1.
    thresholdSaturation: number;
}

/**
 * Details relating to a model for use in the preview.
 */
export type ModelDetails = {
    readonly model?: string;
    readonly contextService: RenderableContextService;
};

/**
 * A renderable context represents the relationship of a texture in the 3D preview
 * with an external canvas. This context allows external clients to render to a texture
 * in the 3D preview with a simple interface.
 */
export type RenderableContext = {
    /**
     * A unique identifier for this renderable context.
     */
    getID(): string;

    /**
     * A name for this renderable.
     */
    getName(): string;

    /**
     * Sets the render context associated to this renderable.
     * @param ctx The context to use for rendering.
     */
    setStaticContext(ctx: CanvasRenderingContext2D): void;

    /**
     * Get the render context associated to this renderable.
     */
    getStaticContext(): CanvasRenderingContext2D | undefined;

    /**
     * Sets whether or not this renderable is dirty and will need re-rendering.
     * @param value The new value
     */
    setStaticContextDirty(value: boolean): void;

    /**
     * When this context has been set as dirty, returns true.
     */
    getStaticContextDirty(): boolean;

    /**
     * A timestamp for the last successful render of the context.
     */
    getLastCompletedStaticRender(): number | undefined;
};

/**
 * Represents a handle to a material in the 3D scene. The underlying complexity of materials is abstracted
 * away so the client doesn't need to know anything more than the ID they're given and the name.
 */
export type MaterialHandle = {
    /**
     * The identifier for the material.
     */
    readonly id: string;
    /**
     * The human readable name for the material.
     */
    readonly name: string;
};

/**
 * Settings related to the 3D preview.
 */
export type PreviewOptions = {
    /**
     * The color expected to be seen in the background of the product. Expects a hexadecimal value.
     */
    readonly backgroundColor?: string;
    /**
     * Image to be used as a background when running with transparency, the image
     * will be scaled and centered to fill the preview based on aspect ratio.
     */
    readonly backgroundImage?: string;
    /**
     * The closest zoom the camera can achieve to the product.
     */
    readonly maxZoomOverride?: number;
    /**
     * The furthest zoom the camera can achieve to the product.
     */
    readonly minZoomOverride?: number;
    /**
     * The environment file used to calculate product lighting.
     */
    readonly environmentFile?: string;
    /**
     * The intensity of the environment lighting.
     */
    readonly environmentIntensity?: number;
    /**
     * The rotation (around the y axis) of the environment lighting, in degrees.
     */
    readonly environmentRotationY?: number;

    /**
     * The lowest point, vertically, that the camera can rotate to.
     * https://doc.babylonjs.com/divingDeeper/cameras/camera_introduction
     */
    readonly lowerBetaLimitDeg?: number;

    /**
     * The highest point, vertically, that the camera can rotate to.
     * https://doc.babylonjs.com/divingDeeper/cameras/camera_introduction
     */
    readonly upperBetaLimitDeg?: number;

    /**
     * The leftmost point, horizontally, that the camera can rotate to.
     * https://doc.babylonjs.com/divingDeeper/cameras/camera_introduction
     */
    readonly lowerAlphaLimitDeg?: number;

    /**
     * The rightmost point, horizontally, that the camera can rotate to.
     * https://doc.babylonjs.com/divingDeeper/cameras/camera_introduction
     */
    readonly upperAlphaLimitDeg?: number;

    /**
     * When set the product while rotate slowly
     */
    readonly autoRotation?: boolean;
    /**
     * Time in milliseconds before the product starts rotating after a user input has taken control.
     */
    readonly idleTimeBeforeRotation?: number;
    /**
     * When set the 3D preview won't attempt to orient the product automatically to its front view at load.
     */
    readonly disableAutomaticOrientation?: boolean;
    /**
     * When true the action bar won't be displayed to the user.
     */
    readonly disableActionBar?: boolean;
    /**
     * When set, mousing over the model in this preview will highlight the associated mesh/material.
     * When layout contexts are provided, only the materials with matching names will highlight.
     */
    readonly highlightOnMaterialHover?: boolean;
    /**
     * Sets the color of highlights when enabled. Expects hexadecimal value.
     */
    readonly highlightColor?: string;
    /**
     * When true, the user will not be able to pan the camera.
     */
    readonly noPan?: boolean;
};

/**
 * Defines the different behaviors supported by the camera system
 * for control when viewing a product.
 */
export enum ProductCameraRig {
    Orbit,
    Pan,
}

/**
 * Identical to CameraAnimation typing, however the
 * target is always available and hence is not optional.
 */
export type CameraPose = {
    readonly lonDeg: number;
    readonly radius: number;
    readonly latDeg: number;
    readonly target: {
        readonly x: number;
        readonly y: number;
        readonly z: number;
    };
};

export type LoadProgressEventData = {
    /**
     * The total load value of the scene, this is an average of all
     * 'in progress' loading events. when all events are fully loaded this value will be 100.
     */
    readonly loadValue: number;
    /**
     * This value is true when the base model and scene have been initialized.
     */
    readonly sceneInitialized: boolean;
};

export interface Design {
    id?: string;
    sku?: string;
    transaction?: Transaction;
    processExecution?: {
        id?: string;
    };
}

/**
 * Someone who has used a workflow experience and entered their contact details.
 */
export interface Customer {
    id?: string;
    emailAddress: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    loginToken?: string;
    partner?: Partner;
    stakeholders?: Stakeholder[];
    bundleStakeholders?: BundleStakeholder[];
    hasBundleTemplates?: boolean;
}

export interface CustomerDetailsInput {
    emailAddress: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
}

export enum StakeholderType {
    Owner = "Owner",
    Approver = "Approver",
    Editor = "Editor",
    Viewer = "Viewer",
}

export interface Stakeholder {
    id: string;
    type?: StakeholderType;
    authorizationDate?: string;
    authorizationNote?: string;
    authorizationStatus?: string;
    transaction?: Transaction;
    customer?: Customer;
}

/**
 * A wrapper for a list of bundles returned from the server.
 */
export interface BundlesFeed {
    /** The items. The size of this array will typically be limited by the request. */
    items: Bundle[];
    /** The total number of items available on the server that matched the initial request. */
    total: number;
}

export enum BundleTemplateStatus {
    Draft = "Draft",
    Active = "Active",
}

export interface Bundle {
    id?: string;
    bundleOwnerId?: string;
    completed?: boolean;
    currentBundleStakeholder?: BundleStakeholder;
    bundleStakeholders?: BundleStakeholder[];
    bundleStateData?: string;
    globalPropertyState?: GlobalPropertyState;
    metadata?: { key: string; value: string }[];
    name?: string;
    partner?: Partner;
    productCollection?: ProductCollectionResource;
    transactions?: Transaction[];
    template?: boolean;
    templateStatus?: BundleTemplateStatus;
    bundleShareActions?: ShareAction[];
    workflowViewerLink: string;
    workflowViewerAmendLink: string;
    workflowViewerReadOnlyLink: string;
    quoteId?: string;
}

export interface BundleStakeholder {
    id: string;
    bundle?: Bundle;
    customer?: Customer;
    type?: StakeholderType;
    authorizationDate?: string;
    authorizationNote?: string;
    authorizationStatus?: string;
}

export interface BundleStakeholderInput {
    type: StakeholderType;
    customerDetails: CustomerDetailsInput;
}

export type ProductCollectionResource = {
    id: string;
    globalPropertyConfiguration?: GlobalPropertyConfiguration;
    productCollectionProducts?: ProductCollectionProductResource[];
    productCollectionProductsFeed?: ProductCollectionProductsFeedResource;
    name: string;
    description?: string;
    image?: Asset;
    transformCollection?: TransformCollection;
};

export type ProductCollectionProductResource = {
    id: string;
    productCollection?: ProductCollectionResource;
    productCollectionId?: string;
    product: Product;
    productId: string;
    workflowId?: string;
};

export type ProductCollectionProductsFeedResource = {
    items: ProductCollectionProductResource[];
    total: number;
};

export type GlobalPropertyState = {
    id: string;
    aspects: GlobalPropertyStateAspect[];
};

export type GlobalPropertyStateAspect = {
    name: string;
    value: string;
    type?: AspectType;
    storage?: GlobalPropertyStateAspectStorage;
    channel?: number;
};

export type GlobalPropertyStateAspectStorage =
    | GlobalPropertyStateFileUploadStorage
    | GlobalPropertyStateColorOptionStorage;

export type GlobalPropertyStateFileUploadStorage = {
    originalAssetKey?: string;
    backgroundRemovedAssetKey?: string;
    useOriginalAsset?: boolean;
    colors?: GlobalPropertyStateIllustrationColor[];
};

export type GlobalPropertyStateIllustrationColor = {
    key: string;
    browserValue: string;
    pmsValue?: string;
};

export type GlobalPropertyStateColorOptionStorage = {
    customColor?: string;
};

export interface BundleStateData {
    transactions: BundleStateTransaction[];
}

export interface BundleStateTransaction {
    transactionId: string;
    /** @deprecated */ transform?: BundleStateTransform;
    activeTransform?: string;
    transforms?: { [key: string]: BundleStateTransform };
}

export interface BundleStateTransform {
    position: Vector3;
    rotation: Vector3;
    scale: Vector3;
}

export interface Vector3 {
    x: number;
    y: number;
    z: number;
}

export interface TransformCollection {
    id: string;
    name: string;
    transforms: TransformCollectionTransform[];
}

export interface TransformCollectionTransform {
    id: string;
    name: string;
    position: Vector3;
    rotation: Vector3;
    scale: Vector3;
}

// Allows a type to be extended with other arbitrary fields, while still requiring the base fields.
export type Extendable<T> = T & { [key: string]: any };

export interface Metafield {
    id: string;
    createdAt: string;
    updatedAt: string;
    entityId: string;
    value: string;
    metafieldConfiguration: MetafieldConfiguration;
}

export interface MetafieldConfiguration {
    id: string;
    createdAt: string;
    updatedAt: string;
    entityType: string;
    metafieldType: string;
    name: string;
}

export enum TextAlgorithm {
    /**
     * An algorithm that finds the best size for text based
     * on a range minSize <-> maxSize
     * NOTE: Default, this is how we've calculated historically.
     */
    Autosize = "Autosize",
    /**
     * An algorithm that follows behavior of other popular
     * text editing applications.
     */
    Traditional = "Traditional",
}

export interface FrameElement extends LayoutElement {
    // The source path for the frame. Equivalent to the d attribute of an SVG path element.
    path: string;

    /**
     * Assigns the 'data-frame-width' attribute to the frame element. The rasterizer we use has issues with images inside
     * patterns. This is a workaround to ensure that the pattern is correctly sized.
     */
    dataWidth?: number;
    /**
     * Assigns the 'data-frame-height' attribute to the frame element. The rasterizer we use has issues with images inside
     * patterns. This is a workaround to ensure that the pattern is correctly sized.
     */
    dataHeight?: number;

    // Disables the placeholder image for this frame. Instead the frame will
    // be blank before any image is UploadRounded.
    disablePlaceholder?: boolean;

    // FIXME: Scale was removed from all other elements however persists on frames
    // to ensure backwards compat with existing functionality. We should remove this when we can.
    scaleX: number;
    scaleY: number;

    // The opacity of the frame
    opacity?: number;
    // The pattern for this frame. Roughly translates to a pattern tag in SVG.
    pattern?: Pattern;
    // Properties used to control focal blur in frames.
    focalBlur?: boolean;
    focalBlurStrength?: number;
    focalBlurRadius?: number;
    forceImageCover?: boolean;

    // Whether to enable a black and white threshold.
    useThreshold?: boolean;
    // If using threshold, whether to invert black and white.
    invertThreshold?: boolean;
    // An integer from 0 to 256.
    threshold?: number;
    // A number from 0 to 1.
    thresholdSaturation?: number;
}

/**
 * A pattern object stores data relevant for displaying an image within a frame, including
 * the source for the image itself and also dimensions and offsets.
 */
interface Pattern extends Omit<LayoutElement, "type"> {
    /**
     * The image to use when a file is generated, this should be an original high resolution source file.
     */
    src: string;

    /**
     * Scaling X of image within frame.
     */
    scaleX: number;

    /**
     * Scaling Y of image within frame.
     */
    scaleY: number;

    /**
     * The SVG document that the src represents. This must be the actual content string of the document, rather than a URL.
     */
    svg?: string;

    /**
     * A map of ID to color code, used to store color selections in the SVG. Only applicable when `svg` is provided.
     */
    colors?: { [key: string]: ColorDefinition };
}

export interface GroupElement extends LayoutElement {
    /**
     * A clip path applied to the group
     */
    clipPath?: string;
    /**
     * Elements renderable inside this group.
     */
    children: LayoutElement[];
}

export interface IllustrationElement extends LayoutElement {
    /**
     * When provided a src will be used to rehyrdrate the svg field. On serialization
     * the svg field will be dropped and only src will remain.
     */
    src?: string;
    /**
     * The string representation of the SVG element. A valid SVG document.
     */
    svg?: string;
    /**
     * A cached image representation of this SVG, to be used for faster
     * rendering in performance critical code.
     */
    cachedObjectURL?: string;
    /**
     * A map of ID to color code, used to store color selections in the SVG.
     */
    colors: { [key: string]: ColorDefinition };
}

/**
 * Represents a raster image on the Papyrus canvas.
 */
export interface ImageElement extends LayoutElement {
    /**
     * A src for the image. This will be used in place of the data URL in server environments and should
     * be original image quality.
     */
    src: string;
    /**
     * https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/preserveAspectRatio
     */
    preserveAspectRatio?: string;
}

export interface TextboxElement extends LayoutElement {
    /**
     * Horizontal alignment of text. eg. left, center, right.
     */
    align?: string;
    /**
     * An image to use as the fill instead of a color.
     */
    textFillImage?: TextFillImage;
    /**
     * Fill color of this text. Any value that can be parsed by css fill should work here.
     */
    fill: string;
    /**
     * The font size.
     */
    fontSize: number;
    /**
     * Information about the font family used to render this text.
     */
    fontData: FontData | undefined;
    /**
     * Text computed for this textbox.
     */
    text?: string;
    /**
     * Per element user input. Not used by the layout engine. Instead
     * used to store user input for textbox to preserve newlines.
     */
    input?: string;
    /**
     * Should the text layout top to bottom.
     */
    vertical?: boolean;
    /**
     * The height between text lines.
     */
    lineHeight?: number;
    /**
     * The alignment of text vertically, top center or bottom.
     */
    verticalAlign: string;
    /**
     * When true the text will be rendered using the provided curve.
     */
    curved?: boolean;
    /**
     * The curve to use when curved = true. Ignored otherwise.
     */
    paths?: string[]; // TODO: remove array and change to path from paths.
    /**
     * The algorithm to use when laying out text.
     */
    algorithm?: TextAlgorithm;

    fillSpotColorDefinition?: SpotColorDefinition;

    strokeColor?: ColorDefinition;
    strokeThickness?: string;
}

export interface FontData {
    assetUrl: string;
    name: string;
}

/**
 * A container for data related to a specific spotcolor. toString
 * can be used to output a string expected by Filegen inside a stroke or fill.
 */
export interface SpotColorDefinition {
    /**
     * The name of the profile being used.
     */
    profileName: string;

    /**
     * The named color expected by the profile.
     */
    namedColor: string;
}

/**
 * Contains configuration for font sizing.
 *
 * When min/maxSize values are set we interpret the textbox as being
 * in "Variable Mode" which essentially
 * auto sizes the text based on a given region.
 *
 * When only size is set, we interpret the textbox as being in "Fixed Mode" where
 * text is computed at a fixed font size.
 */
export interface SizeRange {
    /**
     * Variable Mode: A maximum size
     */
    maxSize?: number;

    /**
     * Variable Mode: Minimum Size
     */
    minSize?: number;

    /**
     * Fixed Mode: A Fixed font size to be used at all times.
     */
    size: number;
}

export interface CanvasRegion {
    top: number;
    left: number;
    width: number;
    height: number;
}

export interface ColorDefinition {
    /**
     * Represents the color value in hex or any other
     * browser supported format. This will be used when rendering the canvas
     * in an environment that doesn't support spot color.
     */
    browserValue: string;

    /**
     * A spot color definition. This is optional and will
     * be applied to the SVG returned by toString when configured allowing
     * filegen to output spot colors.
     */
    spotColor?: SpotColorDefinition;

    /**
     * Represents the color value in the PMS colorspace.
     */
    pmsValue?: string;
}

/**
 * A container for data related to a specific spotcolor. toString
 * can be used to output a string expected by Filegen inside a stroke or fill.
 */
export interface SpotColorDefinition {
    /**
     * The name of the profile being used.
     */
    profileName: string;

    /**
     * The named color expected by the profile.
     */
    namedColor: string;
}

export interface TextFillSpotColor {
    profileName: string;
    namedColor: string;
}

export interface PatternImageData {
    /**
     * The original src of the image.
     */
    src: string;

    /**
     *  The natural width of the image.
     */
    width: number;

    /**
     * The natural height of the image.
     */
    height: number;

    /**
     * Cached result of the calculation width/height.
     */
    aspect: number;

    /**
     * The SVG document that the src represents. This must be the actual content string of the document, rather than a URL.
     */
    svg?: string;

    /**
     * A map of ID to color code, used to store color selections in the SVG. Only applicable when `svg` is provided.
     */
    colors?: { [key: string]: ColorDefinition };
}

/**
 * Defines the direction of scaling, can be used
 * to determine the anchor point of a scale calculation.
 */
export enum ScaleAxis {
    North,
    Northeast,
    East,
    Southeast,
    South,
    Southwest,
    West,
    Northwest,
}

/**
 * A point in a 2D coordinate space.
 */
export interface Point {
    x: number;
    y: number;
}

export interface Workflow {
    defaultPreviewPanelIndex?: number;
    deletedAt?: Date;
    id: string;
    previewAssetKey?: string;
    previewImageUrl?: string;
    name: string;
    introduction: string;
    panels: WorkflowPanel[];
    showModelOnFinishStep: boolean;
    showPricing?: boolean;
    baseCurrency?: string;
    showPlusInVariantPrices?: boolean;
    allowProofDownload: boolean;
    steps: Step<AnyStepData>[];
    stepGroups: StepGroup[];
    theme?: {
        primaryColour: string;
        textColour: string;
    };
    globalPreviewConfig?: PreviewConfiguration;
    finalizeStepConfig?: FinalizeStepConfig;
    partnerId?: string;
    toggleBulkSourceView?: boolean;
    overrideTheme?: Theme;
    globalPropertyConfiguration?: GlobalPropertyConfiguration;
}

export interface WorkflowPanel {
    height: number;
    width: number;
    name: string;
    title?: string;
    index: number;
    transparentBackground?: boolean;
    previewRegion?: PreviewRegion;
    useEditableArea?: boolean;
    editableArea?: {
        x: number;
        y: number;
        height: number;
        width: number;
    };
}

export interface StepGroup {
    id?: string;
    name: string;
    stepNames: string[];
}

export interface FinalizeStepConfig {
    termsMarkdown?: string;
    modelAnimation?: ModelAnimation;
    lookAtAnimation?: CameraAnimation;
    previewRegion?: { left: number; top: number; width: number; height: number };
}

export interface PreviewRegion {
    top: number;
    left: number;
    width: number;
    height: number;
}

export interface ModelAnimation {
    /**
     * A value it seconds along the animation timeline to begin at.
     */
    from?: number;
    /**
     * A value in seconds along the animation timeline to end at.
     */
    to?: number;
    /**
     * When true the animation will loop. The only behaviour currently is to reset
     * back to from but we could have it bounce back and forth and/or follow a curve.
     */
    loop?: boolean;
    /**
     * The name of the animation to play.
     */
    name?: string;
}

/**
 * A CameraAnimation specifies a discrete state that the camera should
 * animate to. This state represents the final position of the camera after animations have run.
 */
export interface CameraAnimation {
    /**
     * The longitutude in degrees the camera should animate to.
     */
    lonDeg: number;
    /**
     * The lattitude in degrees the camera should animnate to.
     */
    latDeg: number;
    /**
     * An optional target for the camera to focus on in the scene.
     */
    target?: { x: number; y: number; z: number };
    /**
     * A value in scene units specifying camera distance from the target.
     */
    radius?: number;
}

/**
 * Settings related to the 3D preview for use at either a global level or at per step level. The global
 * values can be a default if per step values aren't specified.
 */
export interface PreviewConfiguration {
    /**
     * @deprecated Removed in favor of backgroundColor as a simple hexadecimal color value.
     */
    clearColor?: [number, number, number];
    /**
     * The color expected to be seen in the background of the product
     */
    backgroundColor?: string;
    /**
     * The closest zoom the camera can achieve to the product.
     */
    maxZoomOverride?: number;
    /**
     * The furthest zoom the camera can achieve to the product.
     */
    minZoomOverride?: number;
    /**
     * The environment file used to calculate product lighting.
     */
    environmentFile?: string;

    /**
     * The lowest point, vertically, that the camera can rotate to.
     * https://doc.babylonjs.com/divingDeeper/cameras/camera_introduction
     */
    lowerBetaLimitDeg?: number;

    /**
     * The highest point, vertically, that the camera can rotate to.
     * https://doc.babylonjs.com/divingDeeper/cameras/camera_introduction
     */
    upperBetaLimitDeg?: number;

    /**
     * The leftmost point, horizontally, that the camera can rotate to.
     * https://doc.babylonjs.com/divingDeeper/cameras/camera_introduction
     */
    lowerAlphaLimitDeg?: number;

    /**
     * The rightmost point, horizontally, that the camera can rotate to.
     * https://doc.babylonjs.com/divingDeeper/cameras/camera_introduction
     */
    upperAlphaLimitDeg?: number;

    /**
     * When set the product while rotate slowly
     */
    autoRotation?: boolean;
    /**
     * Time in milliseconds before the product starts rotating after a user input has taken control.
     */
    idleTimeBeforeRotation?: number;
    /**
     * When set the 3D preview won't attempt to orient the product automatically to its front view at load.
     */
    disableAutomaticOrientation?: boolean;
    /**
     * When true the action bar won't be displayed to the user.
     */
    disableActionBar?: boolean;

    /**
     * When true the AR button will be displayed to the user.
     */
    enableAR?: boolean;
}

export enum UnitOfMeasurement {
    Pixel = "px",
    Millimeter = "mm",
    Centimeter = "cm",
}

export type PapyrusComponent<T = any> = FunctionComponent<T>;

export type PapyrusNode = ReactNode;

export interface Order {
    id: string;
    internalId: string;
}

export interface OrderItem {
    transactionId: string;
    amountToOrder: number;
}
